/* Card language (2026-08-05 redesign, C1): `Notification.module.css` is
 * the source of truth for every token/class value below — a founder
 * hardware audit flagged the clipboard screen as reading like a different
 * product because this file had drifted off that language. The values
 * here (padding/radius/background/gap, header row shape, action-icon
 * scale) are copied verbatim from `Notification` rather than reinvented.
 * Supersedes the old flat-hairline-row rationale, which was written for
 * the since-deleted `ClipDock` (a nested-surface constraint that no
 * longer exists — Clipboard is a full destination tab now). */
.card {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-12);
  padding: var(--spacing-16);
  border-radius: var(--radius-8);
  background: var(--bg-secondary);
  width: 100%;
  box-sizing: border-box;
}

/* ─── Header row (direction glyph + peer + relative time, delete trailing) ───
 * Mirrors Notification's headerRow/headerMeta shape. */

.headerRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: var(--spacing-8);
}

.headerMeta {
  min-width: 0;
  flex: 1 1 auto;
}

.glyph,
.peerLabel,
.separator,
.timestamp {
  font: var(--text-micro-medium);
  letter-spacing: var(--text-xs-tracking);
}

/* Round 3 (D3): direction carries contrast, not colour. With one paired
 * phone the peer name never varies, so the only LIVE information in this
 * line is direction — the glyph is the sole primary-toned element; peer
 * label, separator and time all recede to tertiary. No colour, no
 * alignment (does not touch R4/C2) — contrast only, and the only piece
 * that's independently stylable without parsing the host's opaque
 * localized `from`/`to` caption string apart from the peer name it embeds. */
.glyph {
  color: var(--text-primary);
}

.peerLabel,
.separator,
.timestamp {
  color: var(--text-tertiary);
}

/* ─── Header actions (reveal/copy/delete — round 3 D1/D2) ───
 * Reveal + copy + delete all moved here from the deleted `.actions` row.
 * Reveal/copy share the tighter `.primaryActions` group; delete sits
 * outside it in this row directly, so the wider `--spacing-16` gap here
 * (vs. `.primaryActions`' `--spacing-8`) separates it from the two
 * most-used controls (D2) — a misclick must not be cheap. */
.headerActions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: var(--spacing-16);
}

.primaryActions {
  display: flex;
  align-items: center;
  gap: var(--spacing-8);
}

/* Round 3 (D2): tertiary tone at rest — demoted from the default
 * `--icon-default` every other IconButton in this card uses. This slot
 * was borrowed from Notification's header-trailing position, where the ✕
 * only dismisses a mirror of something that still exists on the phone;
 * here it destroys the only copy of persisted plaintext, so it reads
 * quieter than copy/reveal at rest despite sharing their shape.
 *
 * Fix round: `.headerActions .deleteButton` (not a bare `.deleteButton`) —
 * a single class is the SAME specificity as IconButton's own base
 * `.iconButton { color: var(--icon-default); }`, so which one wins would
 * depend on which CSS Module happens to load later in the final bundle. The
 * descendant selector combines two classes both scoped to THIS file, same
 * specificity-boost technique `.content.contentMultiline` already uses
 * above, so this demotion holds regardless of import/sheet order. */
.headerActions .deleteButton {
  color: var(--text-tertiary);
}

/* ─── Content ─── */

.content {
  margin: 0;
  /* Body size, body font (C3) — monospace is gone. It was the loudest
   * signal that this screen belonged to a different product, and made
   * ordinary text read as code. */
  font: var(--text-body-regular);
  color: var(--text-primary);
  /* 3-line clamp (C4), replacing the old single-line ellipsis: the clip
   * IS the payload here, not a subtitle, so it gets one more line than
   * Notification's 2-line `.bodyText`. Base case: whitespace (including
   * any embedded newlines) collapses to single spaces and text wraps
   * normally — a single-line clip can never open with blank space.
   * `overflow-wrap: anywhere` forces a break inside an unbroken run (a
   * bare URL has no spaces) so the clamp actually engages instead of one
   * long line overflowing the card. Real multi-line clips get the
   * `.contentMultiline` modifier below instead of preserving newlines
   * here. This is a `<pre>` element (its UA default is `white-space: pre`
   * — no wrap, all whitespace preserved), so `normal` has to be set
   * explicitly to get the collapsing/wrapping behaviour described above. */
  white-space: normal;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.content[data-masked] {
  /* Fix round: D4 originally shipped `--text-body-regular` here (inherited
   * unchanged from `.content` above) at `min-height: 3lh`. That made a
   * SHORT clip 3 lines tall masked and 1 line tall once revealed — reveal
   * SHRANK the card, a jump that didn't exist before D4 and the opposite
   * of what D4 set out to fix. Ruled: reserve exactly ONE content line
   * (`1lh`) — the bullet placeholder is always a single fixed-length line
   * regardless of the real content's length — so revealing can only ever
   * GROW the card (up to the real 3-line clamp), never shrink it. "Looks
   * empty/broken" is instead fixed by making the one line itself
   * substantial: the SAME size real content uses but semibold rather than
   * regular weight (both tokens share the identical 14px/20px metrics —
   * only the weight differs), replacing the faint, undersized-reading row
   * from the dogfood screenshot. Mask height NEVER depends on the real
   * content's length — doing so would leak how long the clip roughly is,
   * a privacy regression dressed as a layout fix. */
  font: var(--text-body-semibold);
  letter-spacing: 0.15em;
  color: var(--text-tertiary);
  user-select: none;
  min-height: 1lh;
}

/* Multi-line clips (the rendered text contains a real newline) honour
 * those line breaks instead of collapsing them to spaces — same modifier
 * pattern as Notification's `.bodyText.bodyTextMultiline`. The clamp
 * relaxes from 3 to 6 (same +3 Notification uses going 2→5) because
 * `pre-line` preserves EVERY explicit newline as its own line, including
 * consecutive ones: a clip pasted from a notes app routinely starts with
 * one or two blank lines, and at the base 3-line budget those blank
 * lines alone could fill the whole card, rendering it apparently empty.
 * The wider budget guarantees real content is still visible. Compound
 * selector keeps specificity above `.content` so the override survives
 * stylesheet reordering, matching Notification's precedent. */
.content.contentMultiline {
  white-space: pre-line;
  -webkit-line-clamp: 6;
}

/* ─── Chips (sensitive/cloud) ───
 * Round 3 (D1): moved out of the deleted `.actions` row into `.headerMeta`
 * above — rare, and `.headerMeta` has no `display: flex` set, so they rely
 * on normal inline text flow to wrap, same as the direction/peer/time
 * spans there. Two fix-round corrections to the original D1 landing (this
 * comment previously — incorrectly — claimed wrapping "just worked"):
 *   1. Wrapping needs an actual line-break OPPORTUNITY, which plain inline
 *      flow only provides at whitespace. The `{" "}` in ClipCard.tsx
 *      supplies that (JSX silently drops a whitespace-only line between
 *      elements, so without an explicit space the chip renders directly
 *      adjacent to the timestamp with nothing between them at all — the
 *      header then had nowhere to break and just overflowed a narrow side
 *      panel). `margin-left` here still substitutes for the flex `gap`
 *      `.actions` used to provide between siblings.
 *   2. `display: inline-flex` (not the plain inline a bare `<span>`
 *      defaults to): a plain inline box's vertical padding doesn't grow
 *      its line box, so the pill visually bled into the line above/below
 *      it. `../Chip/Chip.module.css`'s own `.chip` already guards against
 *      exactly this the same way. */

.chip {
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  font: var(--text-micro-medium);
  padding: var(--spacing-2) var(--spacing-8);
  border-radius: var(--radius-round);
  margin-left: var(--spacing-8);
}

.chip[data-tone="sensitive"] {
  color: var(--text-negative);
  background: var(--bg-tertiary);
}

.chip[data-tone="cloud"] {
  color: var(--text-tertiary);
  background: var(--bg-tertiary);
}

/* Standard sr-only pattern — visually hidden but read by AT. Used for the
 * masked-content hint and the "Copied" live-region announce. */
.srOnly {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}
